import dayjs from "dayjs";
import { get, isEmpty } from "lodash";
import { API_DATE_FORMAT, DATE_FORMAT } from "utils/constants";

export const noSpace = (e) => {
  if (e.key === " ") {
    e.preventDefault();
  }
};

export const bindInputProps = ({
  name,
  values,
  handleChange,
  setFieldTouched,
  touched,
  errors,
  onBlur = () => {},
}) => {
  return {
    value: get(values, name, undefined),
    onChange: handleChange(name),
    onBlur: (e) => {
      const value = get(e, "target.value");
      setFieldTouched(name);
      onBlur(value);
    },
    touched: get(touched, name, false),
    errors: get(errors, name, ""),
    name,
  };
};

/** Formik + Type B text fields — shared Custom input (conseiller) */
export const bindTextFieldInputProps = ({
  name,
  values,
  setFieldValue,
  setFieldTouched,
  touched,
  errors,
  onBlur = () => {},
}) => ({
  type: "custom",
  normalizeText: true,
  value: get(values, name, "") ?? "",
  errors: get(errors, name, ""),
  touched: get(touched, name, false),
  name,
  onChange: ({ target: { value } }) => setFieldValue(name, value),
  onBlur: (e) => {
    setFieldTouched(name, true);
    onBlur(get(e, "target.value"));
  },
});

/** Formik + Type B text fields — Web B2C Simple input (Comparison/*) */
export const bindWebTextFieldInputProps = ({
  name,
  values,
  setFieldValue,
  setFieldTouched,
  handleChange,
  touched,
  errors,
  onBlur = () => {},
}) => ({
  normalizeText: true,
  value: get(values, name, "") ?? "",
  errors: get(errors, name, ""),
  touched: get(touched, name, false),
  name,
  onChange: ({ target: { value } }) => {
    if (setFieldValue) {
      setFieldValue(name, value);
    } else {
      handleChange?.({ target: { name, value } });
    }
  },
  onBlur: (e) => {
    setFieldTouched?.(name, true);
    onBlur(get(e, "target.value"));
  },
});

export const bindDateInputProps = ({
  name,
  values,
  setFieldValue,
  setFieldTouched,
  touched,
  errors,
  onBlur = () => {},
}) => {
  const value = get(values, name, undefined);
  // Keep the same dayjs instance when Formik already stores one. Recreating
  // `dayjs(value)` on every render changes object identity and makes
  // Ant Design's rc-picker reset the open calendar back to the selected month.
  const dateValue = value
    ? dayjs.isDayjs(value)
      ? value
      : dayjs(value)
    : null;

  return {
    // Controlled only — avoid defaultValue, which fights Ant Design's panel
    // while the user navigates months/years with an existing selection.
    value: dateValue,
    onChange: (date) => {
      setFieldValue(
        name,
        date ? date.set("hour", 4).set("minute", 0).set("second", 0) : null,
      );
    },
    onBlur: (e) => {
      if (setFieldTouched) setFieldTouched(name);
      const rawValue = get(e, "target.value");
      // Commit typed dates only when the day actually changes. Focusing the
      // calendar panel blurs the input; rewriting the same day would reset
      // Ant Design's open month/year navigation.
      const parsedDate = dayjs(rawValue, DATE_FORMAT, true);
      if (parsedDate.isValid()) {
        const nextDate = parsedDate
          .set("hour", 4)
          .set("minute", 0)
          .set("second", 0);
        if (!dateValue || !dateValue.isSame(nextDate, "day")) {
          setFieldValue(name, nextDate);
        }
      }
      onBlur(rawValue);
    },
    touched: get(touched, name, false),
    errors: get(errors, name, ""),
    name,
  };
};

export const bindDateRangeInputProps = ({
  name,
  values,
  setFieldValue,
  setFieldTouched,
  touched,
  errors,
  onBlur = () => {},
}) => {
  const [startValue, endValue] = get(values, name, undefined);
  const value = [startValue && dayjs(startValue), endValue && dayjs(endValue)];
  return {
    value,
    defaultValue: value,
    onChange: ([startDate, endDate]) => {
      setFieldValue(name, [
        startDate.format(API_DATE_FORMAT),
        endDate.format(API_DATE_FORMAT),
      ]);
    },
    onBlur: () => {
      setFieldTouched(name);
      onBlur();
    },
    touched: get(touched, name, false),
    errors: get(errors, name, ""),
    name,
  };
};

export const bindArrayInputProps = ({
  parent,
  index,
  name,
  values,
  handleChange,
  setFieldTouched,
  touched,
  errors,
  onBlur = () => {},
}) => {
  const childName = `${parent}.${index}.${name}`;
  return {
    value: get(values, childName, ""),
    onChange: handleChange(childName),
    onBlur: () => {
      setFieldTouched(childName);
      onBlur();
    },
    touched: get(touched, childName, false),
    errors: get(errors, childName, ""),
    name: childName,
  };
};

export const convertToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onload = () => {
      resolve(fileReader.result);
    };
    fileReader.onerror = (error) => {
      reject(error);
    };
  });
};

export const setDefaultIfEmpty = (value, defaultValue) =>
  isEmpty(value) ? defaultValue : value;

export const convertToTouched = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map((item) => convertToTouched(item));
  } else if (obj !== null && typeof obj === "object") {
    let newObj = {};
    for (let key in obj) {
      if (obj.hasOwnProperty(key)) {
        newObj[key] = convertToTouched(obj[key]);
      }
    }
    return newObj;
  } else {
    return true;
  }
};
